import { useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { getChartTheme, CHART_COLORS, formatCompactCurrency, formatFullCurrency, getTooltipStyle, getChartHeight } from './chartTheme';

const CustomTooltip = ({ active, payload, label }) => {
  const theme = getChartTheme();
  if (!active || !payload?.length) return null;

  return (
    <div style={getTooltipStyle(theme)}>
      <p style={{ fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
          <span style={{ width: 10, height: 10, borderRadius: entry.dataKey === 'count' ? '50%' : 2, backgroundColor: entry.color, flexShrink: 0 }} />
          <span style={{ color: theme.textColor }}>{entry.name}:</span>
          <span style={{ fontWeight: 600 }}>
            {entry.dataKey === 'count' ? entry.value?.toLocaleString() : formatFullCurrency(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const CustomLegend = ({ payload }) => {
  const theme = getChartTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px 20px', paddingTop: 8 }}>
      {payload?.map((entry) => (
        <div key={entry.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.labelColor }}>
          <span style={{
            width: 12,
            height: 12,
            borderRadius: entry.dataKey === 'count' ? '50%' : 3,
            backgroundColor: entry.color,
            flexShrink: 0,
          }} />
          {entry.value}
        </div>
      ))}
    </div>
  );
};

/**
 * Historical Opened Liability - All Time by Year
 * ComposedChart with bars (liability) and line (power count), dual Y-axis
 */
const AllTimeChart = ({ data }) => {
  const theme = getChartTheme();
  const chartHeight = useMemo(() => getChartHeight(), []);

  if (!data?.length) return null;

  return (
    <ResponsiveContainer width="100%" height={chartHeight}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />
        <XAxis
          dataKey="year"
          tick={{ fill: theme.textColor, fontSize: 12 }}
          axisLine={{ stroke: theme.gridColor }}
          tickLine={{ stroke: theme.gridColor }}
        />
        <YAxis
          yAxisId="liability"
          tick={{ fill: theme.textColor, fontSize: 12 }}
          axisLine={{ stroke: theme.gridColor }}
          tickLine={{ stroke: theme.gridColor }}
          tickFormatter={formatCompactCurrency}
        />
        <YAxis
          yAxisId="count"
          orientation="right"
          tick={{ fill: theme.textColor, fontSize: 12 }}
          axisLine={{ stroke: theme.gridColor }}
          tickLine={{ stroke: theme.gridColor }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend content={<CustomLegend />} />
        <Bar
          yAxisId="liability"
          dataKey="liability"
          name="Liability"
          fill={CHART_COLORS.primary}
          radius={[4, 4, 0, 0]}
          maxBarSize={36}
        />
        <Line
          yAxisId="count"
          type="monotone"
          dataKey="count"
          name="Power Count"
          stroke={CHART_COLORS.warning}
          strokeWidth={2.5}
          dot={{ r: 3, fill: CHART_COLORS.warning }}
          activeDot={{ r: 5 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default AllTimeChart;
